/**
 * Type-safe обёртки движка автозаявок (housekeeping wave 2026-06-18).
 * Поверх бэка internal/request/autotasks: /v1/request-rules (mgmt CRUD).
 *
 * Триггеры:
 *   stay.checked_out            — авто-заявка на уборку при выселении (тумблер)
 *   room.cleanliness_stale      — плановая уборка по порогу чистоты
 *   recurring_interval          — каждые N дней (полотенца и т.п.)
 *   n_days_after_last_completed — через N дней после последней выполненной
 *
 * Гейтинг — на бэке (tenant-context). Фронт скрывает контролы по роли.
 */
import { customFetch } from './openapi-fetch.js';

/** Триггер автозаявки. */
export type AutoTaskTrigger =
  | 'stay.checked_out'
  | 'room.cleanliness_stale'
  | 'stay.checked_in'
  | 'recurring_interval'
  | 'n_days_after_last_completed';

/** Область применения правила. */
export type AutoTaskScope = 'room' | 'stay' | 'hotel';

/** Правило автозаявки (ruleResponse). */
export interface AutoTaskRule {
  id: string;
  trigger: AutoTaskTrigger;
  target_department: string;
  title_template: string;
  priority: string;
  enabled: boolean;
  category_code?: string;
  item_id?: string;
  interval_days?: number;
  scope: AutoTaskScope;
}

/** Тело создания правила. */
export interface CreateAutoTaskRuleInput {
  trigger: AutoTaskTrigger;
  target_department: string;
  title_template: string;
  priority: string;
  category_code?: string;
  item_id?: string;
  interval_days?: number;
  scope?: AutoTaskScope;
}

/** Тело обновления правила (триггер неизменен). */
export interface UpdateAutoTaskRuleInput {
  target_department: string;
  title_template: string;
  priority: string;
  enabled?: boolean;
  category_code?: string;
  item_id?: string;
  interval_days?: number;
  scope?: AutoTaskScope;
}

/** GET /v1/request-rules — все правила тенанта. */
export async function listAutoTaskRules(): Promise<AutoTaskRule[]> {
  const res = await customFetch<{ rules: AutoTaskRule[] }>({
    url: '/request-rules',
    method: 'GET',
  });
  return res.rules ?? [];
}

/** POST /v1/request-rules — создать правило. */
export async function createAutoTaskRule(
  input: CreateAutoTaskRuleInput,
): Promise<AutoTaskRule> {
  return customFetch<AutoTaskRule>({
    url: '/request-rules',
    method: 'POST',
    data: input,
  });
}

/** PUT /v1/request-rules/{id} — обновить правило (вкл. тумблер enabled). */
export async function updateAutoTaskRule(
  id: string,
  input: UpdateAutoTaskRuleInput,
): Promise<AutoTaskRule> {
  return customFetch<AutoTaskRule>({
    url: `/request-rules/${encodeURIComponent(id)}`,
    method: 'PUT',
    data: input,
  });
}

/** DELETE /v1/request-rules/{id} — удалить правило. */
export async function deleteAutoTaskRule(id: string): Promise<void> {
  await customFetch<void>({
    url: `/request-rules/${encodeURIComponent(id)}`,
    method: 'DELETE',
  });
}
